import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/github/workspace/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const InlineAlert = makeShortcode("InlineAlert");
const CodeBlock = makeShortcode("CodeBlock");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "metrics-api"
    }}>{`Metrics API`}</h1>
    <p>{`The CJA Metrics APIs allow you to retrieve metrics programmatically through Adobe Developer. These APIs use the same data and methods that Adobe uses inside the product UI. See `}<a parentName="p" {...{
        "href": "https://experienceleague.adobe.com/docs/analytics-platform/using/cja-components/apply-create-metrics.html"
      }}>{`Metrics`}</a>{` in the Customer Journey Analytics guide for more information.`}</p>
    <p>{`The endpoints described in this guide are routed through analytics.adobe.io. To use them, you will need to first create a client with access to Customer Journey Analytics APIs. For more information, refer to `}<a parentName="p" {...{
        "href": "https://developer.adobe.com/cja-apis/docs/getting-started/"
      }}>{`Getting started with the Customer Journey Analytics API`}</a>{`.`}</p>
    <InlineAlert variant="info" slots="text" mdxType="InlineAlert" />
    <p>{`Adobe may add optional request and response members (name/value pairs) to existing API objects at any time and without notice or changes in versioning. Adobe recommends that you refer to the API documentation of any third-party tool you integrate with our APIs so that such additions are ignored in processing if not understood. If implemented properly, such additions are non-breaking changes for your implementation. Adobe will not remove parameters or add required parameters without first providing standard notification through release notes.`}</p>
    <p>{`This guide includes instructions for using the following endpoints:`}</p>
    <ul>
      <li parentName="ul">{`GET multiple metrics: Retrieves a list of metrics for a specified dataview ID`}</li>
      <li parentName="ul">{`GET a single metric: Retrieves information for a single metric from a dataview`}</li>
    </ul>
    <h2 {...{
      "id": "retrieve-multiple-metrics"
    }}>{`Retrieve multiple metrics`}</h2>
    <p>{`Use this endpoint to retrieve a single metric for a specified dataview ID.`}</p>
    <p><inlineCode parentName="p">{`GET https://cja.adobe.io/data/dataviews/{DATAVIEW_ID}/metrics`}</inlineCode></p>
    <h3 {...{
      "id": "request-and-response-examples"
    }}>{`Request and response examples`}</h3>
    <p>{`Click the `}<strong parentName="p">{`Request`}</strong>{` tab in the following example to see a cURL request for this endpoint. Click the `}<strong parentName="p">{`Response`}</strong>{` tab to see a successful JSON response for the request.`}</p>
    <CodeBlock slots="heading, code" repeat="2" languages="CURL,JSON" mdxType="CodeBlock" />
    <h4 {...{
      "id": "request"
    }}>{`Request`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-sh"
      }}>{`curl -X GET "https://cja.adobe.io/data/dataviews/{DATAVIEW_ID}/metrics" \\
    -H "x-api-key: {CLIENT_ID}" \\
    -H "x-gw-ims-org-id: {IMS_ORG_ID}" \\
    -H "Authorization: Bearer {ACCESS_TOKEN}"
`}</code></pre>
    <h4 {...{
      "id": "response"
    }}>{`Response`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
    "content": [
        {
            "id": "metrics/adobe_firsttimesessions",
            "name": "First-time Sessions",
            "dataSetType": "event",
            "sourceFieldType": "integer",
            "hasData": true,
            "derivedFieldCompatible": true
        },
        {
            "id": "metrics/adobe_returnsessions",
            "name": "Return Sessions",
            "dataSetType": "event",
            "sourceFieldType": "integer",
            "hasData": true,
            "derivedFieldCompatible": true
        },
        {
            "id": "metrics/visitors",
            "name": "People",
            "dataSetType": "event",
            "hasData": true,
            "derivedFieldCompatible": false
        },
        {
            "id": "metrics/visits",
            "name": "Sessions",
            "dataSetType": "event",
            "hasData": true,
            "derivedFieldCompatible": false
        },
           ],
    "totalElements": 4,
    "totalPages": 1,
    "number": 0,
    "numberOfElements": 4,
    "firstPage": true,
    "lastPage": true,
    "sort": null,
    "size": 4
}
`}</code></pre>
    <h3 {...{
      "id": "request-example-details"
    }}>{`Request example details`}</h3>
    <p>{`The example above shows a request for all metrics of the required `}<inlineCode parentName="p">{`dataviewId`}</inlineCode>{` parameter, as specified in the path.`}</p>
    <h3 {...{
      "id": "response-example-details"
    }}>{`Response example details`}</h3>
    <p>{`The response example above shows the following names of the four metrics returned for the specified dataview:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`First-time Sessions`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Return Sessions`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`People`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Sessions`}</p>
        <p parentName="li">{`It also includes the following information:`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`The `}<a parentName="p" {...{
            "href": "https://experienceleague.adobe.com/docs/analytics-platform/using/cja-connections/create-connection.html#dataset-types"
          }}><inlineCode parentName="a">{`dataSetType`}</inlineCode></a>{` for all four parameters is `}<inlineCode parentName="p">{`event.`}</inlineCode></p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`The metric IDs, the value for the `}<a parentName="p" {...{
            "href": "https://experienceleague.adobe.com/docs/analytics-platform/using/cja-dataviews/derived-fields.html?lang=en#:~:text=Derived%20fields%20are%20an%20important,through%20a%20customizable%20rule%20builder."
          }}><inlineCode parentName="a">{`derivedFieldCompatible`}</inlineCode></a>{` parameter, and the `}<inlineCode parentName="p">{`hasData`}</inlineCode>{` value--indicating whether the metric has data associated with it.`}</p>
      </li>
    </ul>
    <h3 {...{
      "id": "request-parameters"
    }}>{`Request parameters`}</h3>
    <p>{`The GET multiple metrics endpoints includes one request path parameter (`}<inlineCode parentName="p">{`dataviewId`}</inlineCode>{`) and three optional query parameters, as shown below:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Parameter`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Req/Opt`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`dataviewId`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`required`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`string`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The ID of the dataview containing the metric`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`includeType`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`optional`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`array of strings`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A comma-delimited list of include types`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`locale`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`optional`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`string`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The language to use in the response`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`expansion`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`optional`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`array of strings`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A comma-delimited list of additional fields to include in the response. For a complete list of these fields see the `}<a parentName="td" {...{
              "href": "https://developer.adobe.com/cja-apis/docs/api/#tag/Metrics-API/operation/getMetricsForDataview_1"
            }}>{`reference.`}</a></td>
        </tr>
      </tbody>
    </table>
    <h3 {...{
      "id": "response-parameters"
    }}>{`Response parameters`}</h3>
    <p>{`The `}<strong parentName="p">{`Retrieve multiple metrics`}</strong>{` endpoint includes the following response parameters:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Parameter`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`id`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`string`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Metric ID`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`name`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`string`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Metric name`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`description`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`string`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Contents of metric description field in report`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`sourceFieldId`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`string`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`ID of source field`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`sourceFieldName`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`string`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Name of source field`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`storageId`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`string`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`ID of storage`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`dataSetIds`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`array of strings`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Lists the ID for each dataset returned`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`dataSetType`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`array of strings`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`One of three `}<inlineCode parentName="td">{`enum`}</inlineCode>{` dataset types: `}<inlineCode parentName="td">{`EVENT`}</inlineCode>{`, `}<inlineCode parentName="td">{`LOOKUP`}</inlineCode>{`, or `}<inlineCode parentName="td">{`PROFILE`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`schemaType`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`string`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Type of schema`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`sourceFieldType`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`string`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Type of source field`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`tableName`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`string`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Name of table`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`type`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`string`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Metric data type that is listed as an `}<inlineCode parentName="td">{`enum`}</inlineCode>{` of one of the following: `}<inlineCode parentName="td">{`string`}</inlineCode>{`, `}<inlineCode parentName="td">{`boolean`}</inlineCode>{`, `}<inlineCode parentName="td">{`int`}</inlineCode>{`, `}<inlineCode parentName="td">{`decimal`}</inlineCode>{`, `}<inlineCode parentName="td">{`currency`}</inlineCode>{`, `}<inlineCode parentName="td">{`percent`}</inlineCode>{`, `}<inlineCode parentName="td">{`time`}</inlineCode>{`, `}<inlineCode parentName="td">{`enum`}</inlineCode>{`, and `}<inlineCode parentName="td">{`ordered_enum`}</inlineCode>{`.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`required`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`boolean`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Whether it is required`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`hideFromReporting`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`boolean`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Whether the metric is hidden in reports`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`schemaPath`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`string`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Path of schema`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`hasData`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`boolean`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Whether the metric contains data`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`segmentable`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`boolean`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Whether the metric is segmentable`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`favorite`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`boolean`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Whether the metric is tagged as a favorite`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`approved`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`boolean`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Whether the metric is approved`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`tags`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`array of objects`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Contains `}<inlineCode parentName="td">{`property`}</inlineCode>{` objects that contain data associated with the metric. For example, `}<inlineCode parentName="td">{`property1`}</inlineCode>{`, `}<inlineCode parentName="td">{`property2`}</inlineCode>{`, etc. each contain data in key-value pairs for the metric.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`shares`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`array of objects`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Contains `}<inlineCode parentName="td">{`property`}</inlineCode>{` objects that contain data associated with the metric. For example, `}<inlineCode parentName="td">{`property1`}</inlineCode>{`, `}<inlineCode parentName="td">{`property2`}</inlineCode>{`, etc. each contain data in key-value pairs for the metric.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`usageSummary`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`array of objects`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Contains `}<inlineCode parentName="td">{`property`}</inlineCode>{` objects that contain data associated with the metric. For example, `}<inlineCode parentName="td">{`property1`}</inlineCode>{`, `}<inlineCode parentName="td">{`property2`}</inlineCode>{`, etc. each contain data in key-value pairs for the metric.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`notFound`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`boolean`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Whether the metric is found`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`hidden`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`boolean`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Whether the metric is hidden`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`fromGlobalLookup`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`boolean`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Whether the metric is sourced from global lookup`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`multiValued`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`boolean`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Whether the metric has multiple values`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`includeExcludeSetting`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`object`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Shows settings specified for metric retrieval. This includes the following: `}<inlineCode parentName="td">{`caseSensitive`}</inlineCode>{`, `}<inlineCode parentName="td">{`match`}</inlineCode>{`, `}<inlineCode parentName="td">{`type`}</inlineCode>{`, `}<inlineCode parentName="td">{`rules`}</inlineCode>{` and `}<inlineCode parentName="td">{`enabled`}</inlineCode>{`. These settings are described in the following rows.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`caseSensitive`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`boolean`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Whether the metric is queried as case sensitive. This is contained in the `}<inlineCode parentName="td">{`includeExcludeSetting`}</inlineCode>{` response object.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`match`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`string`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The logical operator specified: `}<inlineCode parentName="td">{`and`}</inlineCode>{`, or `}<inlineCode parentName="td">{`or`}</inlineCode>{` (both type `}<inlineCode parentName="td">{`enum`}</inlineCode>{`). This is contained in the `}<inlineCode parentName="td">{`includeExcludeSetting`}</inlineCode>{` response object.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`type`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`string`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The type specified: `}<inlineCode parentName="td">{`string`}</inlineCode>{`, `}<inlineCode parentName="td">{`numeric`}</inlineCode>{` or `}<inlineCode parentName="td">{`date`}</inlineCode>{` (all type `}<inlineCode parentName="td">{`enum`}</inlineCode>{`). This is contained in the `}<inlineCode parentName="td">{`includeExcludeSetting`}</inlineCode>{` response object.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`rules`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`array of objects`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Contains the rules specified for the query: This includes `}<inlineCode parentName="td">{`string`}</inlineCode>{` and `}<inlineCode parentName="td">{`value`}</inlineCode>{` (both type `}<inlineCode parentName="td">{`string`}</inlineCode>{`). This is contained in the `}<inlineCode parentName="td">{`includeExcludeSetting`}</inlineCode>{` response object.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`enabled`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`boolean`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Whether the query specifies that the metric is enabled. This is contained in the `}<inlineCode parentName="td">{`includeExcludeSetting`}</inlineCode>{` response object.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`fieldDefinition`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`array of objects`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Shows field definitions for `}<inlineCode parentName="td">{`func`}</inlineCode>{`, `}<inlineCode parentName="td">{`id`}</inlineCode>{`, `}<inlineCode parentName="td">{`field`}</inlineCode>{`, `}<inlineCode parentName="td">{`label`}</inlineCode>{`, `}<inlineCode parentName="td">{`branches`}</inlineCode>{`, `}<inlineCode parentName="td">{`oberon-storage-type`}</inlineCode>{`, `}<inlineCode parentName="td">{`oberon-table`}</inlineCode>{`, `}<inlineCode parentName="td">{`oberon-field`}</inlineCode>{`, `}<inlineCode parentName="td">{`oberon-storage-id`}</inlineCode>{`, `}<inlineCode parentName="td">{`case-sensitive`}</inlineCode>{`, and `}<inlineCode parentName="td">{`mapped-type`}</inlineCode>{`. These definitions are described in the following rows.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`func`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`string`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The function of the field, including options: `}<inlineCode parentName="td">{`raw_field`}</inlineCode>{`, `}<inlineCode parentName="td">{`match`}</inlineCode>{` or `}<inlineCode parentName="td">{`floor`}</inlineCode>{` (all type `}<inlineCode parentName="td">{`enum`}</inlineCode>{`). This is contained in the `}<inlineCode parentName="td">{`fieldDefinition`}</inlineCode>{` response object.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`id`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`string`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The ID of the field. This is contained in the `}<inlineCode parentName="td">{`fieldDefinition`}</inlineCode>{` response object.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`field`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`string`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The field associated with the `}<inlineCode parentName="td">{`fieldDefinition`}</inlineCode>{`. This is contained in the `}<inlineCode parentName="td">{`fieldDefinition`}</inlineCode>{` response object.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`label`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`string`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The label. This is contained in the `}<inlineCode parentName="td">{`fieldDefinition`}</inlineCode>{` response object.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`branches`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`array of objects`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Shows branches for `}<inlineCode parentName="td">{`pred`}</inlineCode>{` and `}<inlineCode parentName="td">{`map-to`}</inlineCode>{`.  This is contained in the `}<inlineCode parentName="td">{`fieldDefinition`}</inlineCode>{` response object. For the complete data model on  `}<inlineCode parentName="td">{`pred`}</inlineCode>{` objects and parameters, see the `}<a parentName="td" {...{
              "href": "https://developer.adobe.com/cja-apis/docs/api/#tag/Metrics-API/operation/getMetricsForDataview_1"
            }}>{`CJA Metrics API reference`}</a>{`.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`oberon-storage-type`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`string`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The type used in oberon storage. This is contained in the `}<inlineCode parentName="td">{`fieldDefinition`}</inlineCode>{` response object.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`oberon-table`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`string`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The table used in oberon. This is contained in the `}<inlineCode parentName="td">{`fieldDefinition`}</inlineCode>{` response object.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`oberon-field`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`string`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The field in oberon. This is contained in the `}<inlineCode parentName="td">{`fieldDefinition`}</inlineCode>{` response object.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`oberon-storage-id`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`string`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The ID associated with oberon storage. This is contained in the `}<inlineCode parentName="td">{`fieldDefinition`}</inlineCode>{` response object.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`case-sensitive`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`boolean`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Whether the `}<inlineCode parentName="td">{`fieldDefinition`}</inlineCode>{` is case-sensitive. This is contained in the `}<inlineCode parentName="td">{`fieldDefinition`}</inlineCode>{` response object.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`mapped-type`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`string`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The type used for mapping. This is contained in the `}<inlineCode parentName="td">{`fieldDefinition`}</inlineCode>{` response object.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`countType`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`string`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Contains the enums `}<inlineCode parentName="td">{`instances`}</inlineCode>{` and `}<inlineCode parentName="td">{`values`}</inlineCode>{`.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`formatSetting`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`object`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The options for formatting some metrics. Includes `}<inlineCode parentName="td">{`polarity`}</inlineCode>{`, `}<inlineCode parentName="td">{`formatType`}</inlineCode>{`, `}<inlineCode parentName="td">{`decimalPlaces`}</inlineCode>{`, and `}<inlineCode parentName="td">{`currency`}</inlineCode>{`. For the complete data model on these settings, see the `}<a parentName="td" {...{
              "href": "https://developer.adobe.com/cja-apis/docs/api/#tag/Metrics-API/operation/getMetricsForDataview_1"
            }}>{`CJA Metrics API reference`}</a>{`.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`attributionSetting`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`object`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The options for setting attribution. Includes `}<inlineCode parentName="td">{`enabled`}</inlineCode>{`, `}<inlineCode parentName="td">{`attributionModel`}</inlineCode>{`, and `}<inlineCode parentName="td">{`lookback`}</inlineCode>{`. For the complete data model on these settings, see the `}<a parentName="td" {...{
              "href": "https://developer.adobe.com/cja-apis/docs/api/#tag/Metrics-API/operation/getMetricsForDataview_1"
            }}>{`CJA Metrics API reference`}</a>{`.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`precision`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`int32`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The number of digits allowed for numeric parameters`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`isDeleted`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`boolean`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Whether the metric is deleted`}</td>
        </tr>
      </tbody>
    </table>
    <h2 {...{
      "id": "retrieve-a-single-metric"
    }}>{`Retrieve a single metric`}</h2>
    <p>{`You can retrieve details of a single metric if you know the metric ID. You can find the metric ID by using the multiple metrics endpoint, as described above.`}</p>
    <p>{`Use this endpoint to retrieve a list of metrics for a specified dataview ID.`}</p>
    <p><inlineCode parentName="p">{`GET https://cja.adobe.io/data/dataviews/{DATAVIEW_ID}/metrics/{METRIC_ID}`}</inlineCode></p>
    <h3 {...{
      "id": "request-and-response-examples-1"
    }}>{`Request and response examples`}</h3>
    <p>{`Click the `}<strong parentName="p">{`Request`}</strong>{` tab in the following example to see a cURL request for this endpoint. Click the `}<strong parentName="p">{`Response`}</strong>{` tab to see a successful JSON response for the request.`}</p>
    <CodeBlock slots="heading, code" repeat="2" languages="CURL,JSON" mdxType="CodeBlock" />
    <h4 {...{
      "id": "request-1"
    }}>{`Request`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-sh"
      }}>{`curl -X GET "https://cja.adobe.io/data/dataviews/dv_64c7cf/metrics/visits" \\
    -H "x-api-key: {CLIENT_ID}" \\
    -H "x-gw-ims-org-id: {IMS_ORG_ID}" \\
    -H "Authorization: Bearer {ACCESS_TOKEN}"
`}</code></pre>
    <h4 {...{
      "id": "response-1"
    }}>{`Response`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
    "id": "metrics/visits",
    "name": "Sessions",
    "dataSetType": "event",
    "hasData": true,
    "derivedFieldCompatible": false
}
`}</code></pre>
    <h3 {...{
      "id": "request-example-details-1"
    }}>{`Request example details`}</h3>
    <p>{`The request example above shows a request for information associated with the `}<inlineCode parentName="p">{`visits`}</inlineCode>{` metric in the `}<inlineCode parentName="p">{`dv_64c7cf`}</inlineCode>{` data view.`}</p>
    <h3 {...{
      "id": "response-example-details-1"
    }}>{`Response example details`}</h3>
    <p>{`The example response above shows information for the `}<inlineCode parentName="p">{`visits`}</inlineCode>{` metric in the `}<inlineCode parentName="p">{`dv_64c7cf`}</inlineCode>{` data view, including the following:`}</p>
    <ul>
      <li parentName="ul">{`The metric name is `}<inlineCode parentName="li">{`Sessions`}</inlineCode>{`.`}</li>
      <li parentName="ul">{`The setting for the metric data type is `}<inlineCode parentName="li">{`event`}</inlineCode>{`.`}</li>
      <li parentName="ul">{`The metric has data associated with it, but is not defined as a `}<a parentName="li" {...{
          "href": "https://experienceleague.adobe.com/docs/analytics-platform/using/cja-dataviews/derived-fields.html"
        }}>{`derived field`}</a>{`.`}</li>
    </ul>
    <h3 {...{
      "id": "request-parameters-1"
    }}>{`Request parameters`}</h3>
    <p>{`The required request parameters for retrieving a single metric should be specified in the path. The optional parameters should be specified as query parameters.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Parameter`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Req/Opt`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`dataviewId`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`required`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`string`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The ID of the dataview containing the dimension`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`metricId`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`required`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`string`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The ID of the metric. When specifying, do not include the `}<inlineCode parentName="td">{`metrics/`}</inlineCode>{` prefix. For example, instead of `}<inlineCode parentName="td">{`metrics/visitors`}</inlineCode>{`, use only `}<inlineCode parentName="td">{`visitors`}</inlineCode>{` for the ID`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`locale`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`optional`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`string`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The language to use in the response`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`expansion`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`optional`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`array of strings`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A comma-delimited list of additional fields to include in the response. For a complete list of these fields see the `}<a parentName="td" {...{
              "href": "https://developer.adobe.com/cja-apis/docs/api/#tag/Metrics-API/operation/getMetricById_1"
            }}>{`reference.`}</a></td>
        </tr>
      </tbody>
    </table>
    <h3 {...{
      "id": "response-parameters-1"
    }}>{`Response parameters`}</h3>
    <p>{`The response parameters for retrieving a single metric are the same as for retrieving multiple metrics. See the table above for a description of each parameter.`}</p>
    <h2 {...{
      "id": "api-status-codes"
    }}>{`API status codes`}</h2>
    <p>{`For a description of API status codes and tips for troubleshooting, see the `}<a parentName="p" {...{
        "href": "https://experienceleague.adobe.com/docs/experience-platform/landing/troubleshooting.html#api-status-codes"
      }}>{`Platform FAQ and troubleshooting guide`}</a>{`.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      